/**
 * Input Area field to provide multi line Input
 *
 * @module views/components/InputArea
 * @memberof -Common
 */
import './InputArea.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { ErrorMessage, useField } from 'formik';
import PropTypes from 'prop-types';

import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import ResponseMessages from '@ulta/core/components/ResponseMessages/ResponseMessages';
import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';


/**
  * A click handler function
  * @method
  * @param {Object} methods - Passing setIsActive, setIsFocused,  handleFocus in  methods as arguments
  */
export const handleFocus = ( methods ) => {
  methods.setIsActive( true );
  methods.setIsFocused( true );

  if( methods.handleFocus ){
    methods.handleFocus();
  }
};

/**
  * A click handler function
  * @method
  * @param {Object} methods - Passing setIsFocused method as an argument
  */
export const handleBlur = ( methods, data ) => {
  const { setIsFocused, fieldOnBlur } = methods || {};
  const { event } = data || {};

  if( !event ){
    return;
  }
  setIsFocused( false );
  // Formik set touched flag on blur event on textArea instead of change event.
  fieldOnBlur( event );
};

/**
  * A click handler function
  * @method
  * @param {Object} methods - Passing setIsActive, setIsWarning as methods and isWarning, field, props as data and an event as arguments
  */
export const handleChange = ( methods, data ) => {
  const event = { ...data.event };
  if( event.target.maxLength > 0 ){
    event.target.value = event.target.value.slice( 0, event.target.maxLength );
  }
  methods.setIsActive( true );
  if( data.isWarning ){
    methods.setIsWarning( false );
  }
  methods.fieldOnChange( event );
  methods.propsOnChange && methods.propsOnChange( event.target.value );
};

/**
  * Represents a InputArea component
  *
  * @method
  * @param {InputAreaProps} props - React properties passed from composition
  * @returns InputArea
  */
export const InputArea = function( props ){
  const {
    autoCorrect = defaultProps.autoCorrect,
    spellCheck = defaultProps.spellCheck,
    showValidationMessage = defaultProps.showValidationMessage
  } = props;
  const [field, meta] = useField( props );
  const [isFocused, setIsFocused] = useState( false );
  const [isActive, setIsActive] = useState( false );
  const [isWarning, setIsWarning] = useState( false );
  const inputEl = useRef( props.name );

  const handleFocusMethods = {
    setIsActive,
    setIsFocused,
    handleFocus: props.handleFocus
  };

  const handleChangeMethod = {
    setIsActive,
    setIsWarning,
    propsOnChange: props.onChange,
    fieldOnChange: field.onChange
  };
  const handleChangeData = {
    isWarning
  };

  const handleBlurMethod = {
    setIsFocused,
    fieldOnBlur: field.onBlur
  };

  useEffect( () => {
    if( showValidationMessage ){
      if( props.warningMessage && !isActive ){
        setIsWarning( true );
      }
    }
  }, [props.warningMessage, showValidationMessage, isActive] );

  return (
    <div
      className={
        classNames(
          'InputArea', {
            'InputArea--error': !isActive && meta.touched && meta.error && !props.disabled,
            'InputArea--disable': props.disabled
          }
        )
      }
    >
      <div
        className={ 'InputArea__label' }
      >
        <label
          htmlFor={ field.name }
          className='InputArea__label--text'
        >
          <Text
            textStyle='body-2'
            htmlTag='p'
          >
            { props.label }
          </Text>
        </label>
      </div>
      <div
        className={
          classNames(
            'InputArea__content', {
              'InputArea__content--active': isActive || ( !isActive && ( field.value !== '' ) ),
              'InputArea__content--focused': isFocused,
              'InputArea--error': ( !isFocused && meta.touched && meta.error && !props.disabled )
            }
          )
        }
      >
        <textarea
          { ...spreadProps( { ...field } ) }
          className={
            classNames(
              'InputArea__textarea',
              {
                'InputArea__textarea--active': isActive || ( !isActive && ( field.value !== '' ) )
              }
            )
          }
          placeholder={ props.placeholder }
          id={ field.name }
          maxLength={ props.maxLength }
          disabled={ props.disabled }
          value={ field.value || '' }
          onChange={ ( event ) => {
            handleChange( handleChangeMethod, { ...handleChangeData, event } );
          } }
          onFocus={ () => {
            handleFocus( handleFocusMethods );
          } }
          onBlur={ ( event ) => {
            handleBlur( handleBlurMethod, { event } );
          } }
          ref={ inputEl }
          autoCorrect={ autoCorrect }
          spellCheck={ spellCheck }
          { ...spreadProps( {
            'aria-describedby': props.ariaId
          } ) }
        />
      </div>
      { props.instructionalText &&
        <div
          className='InputArea__instructionalText'
          { ...spreadProps( {
            'aria-live': props.instructionalTextLive,
            id: props.ariaId
          } ) }
        >
          <Text
            textStyle='body-3'
            color='neutral-600'
            htmlTag='span'
          >
            { props.instructionalText }
          </Text>
        </div>
      }
      { ( () => {
        if( props.warningMessage && !isFocused ){
          return (
            <ErrorMessage
              name={ field.name }
              render={ () => (
                <ResponseMessages messageType='warning'
                  inline={ true }
                  message={ props.warningMessage }
                />
              ) }
            />
          );
        }
        else if( !isFocused && meta.touched && meta.error && !props.disabled ){
          return (
            <ErrorMessage
              name={ field.name }
              render={ msg => {
                return (
                  <InlineMessage messageType='error'
                    inline={ true }
                    message={ msg }
                  />
                );
              } }
            />

          );
        }
      } )() }
    </div>
  );
};

/**
  * Property type definitions
  * @typedef InputAreaProps
  * @type {object}
  * @property {string} example - refactor or delete
  */
export const propTypes =  {
  /** Label of the input to render over the inputbox */
  label: PropTypes.string,
  /** The input field placeholder attribute value */
  placeholder: PropTypes.string,
  /** Sets the maximum length attribute value for the input field  */
  maxLength: PropTypes.number,
  /** Instructional text displayed below the input field */
  instructionalText: PropTypes.string,
  /** Flag to disable input field  */
  disabled: PropTypes.bool,
  /** Field-level warning messaging (used with the ResponseMessages component) */
  warningMessage: PropTypes.string,
  /** Sets the input field's autocorrect attribute value */
  autoCorrect: PropTypes.string,
  /** Flag to sets the spellcheck attribute, which defines whether the input may be checked for spelling errors. */
  spellCheck: PropTypes.bool,
  /** Flag to show or hide the ResponseMessages component */
  showValidationMessage: PropTypes.bool,
  /** aria-live attribute to announce characters remaining */
  instructionalTextLive: PropTypes.oneOf( [
    'polite',
    'assertive'
  ] ),
  /** ariaId to describe the description of helper text */
  ariaId: PropTypes.string
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {string} autoCorrect='on' - The input field autocorrect attribute, default is 'on'
  * @property {boolean} spellCheck=true - Flag to trigger spellcheck in the field, default is 'true'
  * @property {boolean} showValidationMessage=true - Flag to show or hide the ResponseMessages component, default is 'true'
  */
export const defaultProps =  {
  autoCorrect: 'on',
  spellCheck: true,
  showValidationMessage: true
};

InputArea.propTypes = propTypes;
InputArea.defaultProps = defaultProps;

export default InputArea;
